import { useAxios } from "../useAxios"

export const useConversationsRequests = () => {
    const { sendRequest } = useAxios()

    const route = '/inbox/conversations'

    const getConversationByConversationId = (id: any) => {
        return sendRequest({
            slug: `getConversationByConversationId`,
            url: `${route}/getConversationByConversationId/${id}`,
            method: 'GET',
        })
    }

    const updateConversations = (payload: any) => {
        return sendRequest({
            slug: `updateConversations`,
            url: `${route}/bulk/actions/update`,
            method: 'PATCH',
            payload,
            displayToast: false
        })
    }

    const createNewConversation = (payload: any) => {
        return sendRequest({
            slug: `createNewConversation`,
            url: `${route}/createNewConversation`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const paginateConversations = (payload: { page: number; filters?: any; isSales?: boolean }) => {
        // Per-page + per-filter slug so tab changes do NOT abort each other while still
        // deduping the exact same page/scope/filter request.
        const scope = payload.isSales === true ? 'sn' : payload.isSales === false ? 'in' : 'all'
        const filterScope = payload.filters ? JSON.stringify(payload.filters) : 'none'
        return sendRequest({
            slug: `paginateConversations:${payload.page}:${scope}:${filterScope}`,
            url: `${route}/paginate`,
            method: 'GET',
            params: {
                page: payload.page,
                ...(payload.isSales !== undefined ? { isSales: payload.isSales } : {}),
                ...(payload.filters && { filters: JSON.stringify(payload.filters) })
            },
            displayToast: false
        })
    }

    const getPinnedConversations = () => {
        return sendRequest({
            slug: `getPinnedConversations`,
            url: `${route}/pinned`,
            method: 'GET',
            displayToast: false
        })
    }

    const deleteConversation = (conversationId: string) => {
        return sendRequest({
            slug: `deleteConversation`,
            url: `${route}/${conversationId}`,
            method: 'DELETE',
            displayToast: false
        })
    }

    const starConversation = (conversationId: string, isStarred: boolean) => {
        return sendRequest({
            slug: `starConversation`,
            url: `${route}/${conversationId}/star`,
            method: 'PATCH',
            payload: { isStarred },
            displayToast: false
        })
    }

    const starConversations = (conversationIds: string[], isStarred: boolean) => {
        return sendRequest({
            slug: `starConversations`,
            url: `${route}/bulk/actions/star`,
            method: 'PATCH',
            payload: { conversations: conversationIds, isStarred },
            displayToast: false
        })
    }

    const pinConversation = (conversationId: string, isPinned: boolean) => {
        return sendRequest({
            slug: `pinConversation`,
            url: `${route}/${conversationId}/pin`,
            method: 'PATCH',
            payload: { isPinned },
            displayToast: false
        })
    }

    const pinConversations = (conversationIds: string[], isPinned: boolean) => {
        return sendRequest({
            slug: `pinConversations`,
            url: `${route}/bulk/actions/pin`,
            method: 'PATCH',
            payload: { conversations: conversationIds, isPinned },
            displayToast: false
        })
    }

    const getFilteringOptions = () => {
        return sendRequest({
            slug: `getInboxFilteringOptions`,
            // Backend route name (keep backend alias for older clients)
            url: `${route}/getListOfApplicableConditionFilters`,
            method: 'GET',
            displayToast: false
        })
    }

    /**
     * GET /inbox/queryParams — vraća LinkedIn voyagerMessagingGraphQL parametre koji su
     * potrebni za INBOX_GET_CONVERSATIONS_V1 (queryId/searchQueryId/profileId/timestamp/...).
     */
    const getInboxQueryParams = () => {
        return sendRequest({
            slug: `getInboxQueryParams`,
            url: `/inbox/queryParams`,
            method: 'GET',
            displayToast: false
        })
    }

    const getRedirectedConversation = (connectionId: string) => {
        return sendRequest({
            slug: `getRedirectedConversation`,
            url: `${route}/redirectedConversation`,
            method: 'POST',
            payload: { connectionId },
            displayToast: false
        })
    }

    const applyTagsToConversations = (payload: { tags: any[]; connectionIds: string[]; conversationIds: string[]; shouldRefetchTags?: boolean }) => {
        return sendRequest({
            slug: `applyTagsToConversations`,
            url: `${route}/bulk/actions/tags/apply`,
            method: 'PATCH',
            payload,
            displayToast: false
        })
    }

    const removeTagsFromConversations = (payload: { tags: string[]; connectionIds: string[]; conversationIds: string[] }) => {
        return sendRequest({
            slug: `removeTagsFromConversations`,
            url: `${route}/bulk/actions/tags/remove`,
            method: 'PATCH',
            payload,
            displayToast: false
        })
    }

    const applyRemoveTagsFromConversations = (payload: { tagsToAdd: any[]; tagsToRemove: string[]; connectionIds: string[]; conversationIds: string[] }) => {
        return sendRequest({
            slug: `applyRemoveTagsFromConversations`,
            url: `${route}/tags/apply-remove`,
            method: 'POST',
            payload,
            displayToast: false
        })
    }

    const getConnectionForConversation = (profileId: string) => {
        return sendRequest({
            slug: `getConnectionForConversation`,
            url: `/connections/profileId/${profileId}`,
            method: 'GET',
            displayToast: false
        })
    }

    const updateConversationStar = (id: string, isStarred: boolean) => {
        return sendRequest({
            slug: `updateConversationStar`,
            url: `${route}/${id}/star`,
            method: 'PATCH',
            payload: { isStarred },
            displayToast: false
        })
    }

    const snoozeConversations = (conversationIds: string[], snoozedUntil: number | null) => {
        return sendRequest({
            slug: `snoozeConversations`,
            url: `${route}/bulk/actions/snooze`,
            method: 'PATCH',
            payload: { conversations: conversationIds, snoozedUntil },
            displayToast: false
        })
    }

    const getConversationsTagStatus = (conversationIds: string[]) => {
        return sendRequest({
            slug: `getConversationsTagStatus`,
            url: `${route}/tag-status`,
            method: 'GET',
            params: { conversationIds: conversationIds.join(',') },
            displayToast: false
        })
    }

    return {
        getConversationByConversationId,
        updateConversations,
        createNewConversation,
        updateConversationStar,
        paginateConversations,
        getPinnedConversations,
        deleteConversation,
        starConversation,
        starConversations,
        pinConversation,
        pinConversations,
        getFilteringOptions,
        getInboxQueryParams,
        getRedirectedConversation,
        applyTagsToConversations,
        removeTagsFromConversations,
        applyRemoveTagsFromConversations,
        getConnectionForConversation,
        snoozeConversations,
        getConversationsTagStatus,
    };
}
